import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * MathText component - renders LaTeX using MathJax (loaded globally via CDN)
 * Properly triggers MathJax to process the content
 * Also handles newline characters (\n) by converting them to line breaks
 */
export default function MathText({ children }) {
  const containerRef = useRef(null);
  const [error, setError] = useState(false);
  const [key, setKey] = useState(0); // Force re-render when content changes
  const [isReady, setIsReady] = useState(false);
  const processingRef = useRef(false);
  const mountedRef = useRef(true);

  // Force re-render when children changes to clear previous MathJax content
  useEffect(() => {
    setKey(prev => prev + 1);
    setError(false);
    setIsReady(false);
  }, [children]);

  // Track mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const processWithMathJax = useCallback(async () => {
    // Early exit if unmounted or already processing
    if (!mountedRef.current) return true;
    if (processingRef.current) return false;
    if (!containerRef.current) return false;
    
    // Check if MathJax is fully loaded and ready
    if (!window.MathJax?.typesetPromise) {
      return false;
    }

    processingRef.current = true;
    
    try {
      // Double-check we're still mounted and element exists
      if (!mountedRef.current || !containerRef.current) {
        return true;
      }
      
      // Clear any previous MathJax rendering - wrap in try-catch
      if (window.MathJax.typesetClear && containerRef.current) {
        try {
          window.MathJax.typesetClear([containerRef.current]);
        } catch (clearErr) {
          // Ignore clear errors - element may have been removed
        }
      }
      
      // Check again before typesetting
      if (!mountedRef.current || !containerRef.current) {
        return true;
      }
      
      await window.MathJax.typesetPromise([containerRef.current]);
      
      if (mountedRef.current) {
        setIsReady(true);
      }
      return true;
    } catch (err) {
      // Only log non-removal errors
      if (!err.message?.includes('removeChild') && !err.message?.includes('not found')) {
        console.error('MathJax typeset error:', err);
      }
      if (mountedRef.current) {
        setError(true);
      }
      return true; // Return true to stop polling
    } finally {
      processingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let intervalId = null;
    let timeoutId = null;
    let localMounted = true;

    const attemptProcess = async () => {
      if (!localMounted || !mountedRef.current) return;
      
      // Ensure MathJax is loaded first
      if (window.loadMathJax && !window.MathJax) {
        window.loadMathJax();
      }
      
      const success = await processWithMathJax();
      if (success && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Try immediately
    attemptProcess();

    // If not ready, poll every 50ms (faster polling for better responsiveness)
    if (!isReady && !error) {
      intervalId = setInterval(attemptProcess, 50);
      
      // Stop polling after 8 seconds
      timeoutId = setTimeout(() => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 8000);
    }

    return () => {
      localMounted = false;
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [children, key, processWithMathJax, isReady, error]);

  if (!children) {
    return <span style={{ color: 'orange' }}>(empty)</span>;
  }
  
  if (typeof children !== 'string') {
    return <span>{children}</span>;
  }

  // DO NOT replace \$ - let MathJax handle escaped dollar signs
  // MathJax is configured with processEscapes: true, so \$ will render as $
  const processedText = children;

  // Return text in a span that MathJax will process
  // If error, show raw text with error indicator
  if (error) {
    return <span style={{ color: 'darkred' }}>{processedText} [MathJax Error]</span>;
  }

  // Split text by newlines and render with proper line breaks
  const lines = processedText.split('\n');
  
  return (
    <span key={key} ref={containerRef} style={{ whiteSpace: 'pre-wrap' }}>
      {lines.map((line, index) => (
        <React.Fragment key={`line-${index}`}>
          {line}
          {index < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </span>
  );
}
